import moment from "moment";
import { bulkNew } from "utility/firestoreManager";
import FirestoreBase from "models/base";
import { SELF_SERVE_TENANT_ID } from "types/onboarding";
import { DEFAULT_SIGIL_LINKS } from "types/configs";
import {
  CHANNEL_EMAIL,
  COMM_CLASS_CUSTOMER_DIRECT,
  COMM_SUBCLASS_AUTH_PRICE_WARNING,
} from "./sellerCommunications";
import { buildMainBody, stepDetails } from "./selfServeAlerts";

const COLLECTION_NAME = "messages";
const MESSAGE_TYPE_LAUNCH_CASES = "launchCases";
const MESSAGE_TYPE_FOREVER_LAUNCH_ENABLED = "foreverLaunchEnabled";
const MESSAGE_TYPE_GENERIC = "message";
const MESSAGE_EVENT_EXPORT = "exportData";
const MESSAGE_EVENT_PRODUCT_IMPORT = "productImport";
const MESSAGE_STATUS_SUCCESS = "success";
const MESSAGE_STATUS_FAILED = "failed";
const MESSAGE_EVENT_EXPORT_AUTHORIZED_CASES = "exportAuthorizedCases";
const MESSAGE_EVENT_EXPORT_PRODUCTS = "exportProducts";
const MESSAGE_EVENT_EXPORT_SELLERS = "exportSellers";
const MESSAGE_EVENT_SELLER_COMMUNICATION = "sellerCommunication";
const MESSAGE_EVENT_PRODUCT_UPDATE = "productUpdate";
const MESSAGE_EVENT_PRODUCT_DELETE = "productDelete";
const MESSAGE_EVENT_OPS_NOTIFICATION = "sendOpsNotification";
const MESSAGE_EVENT_SELLER_AUTHORIZED_NOTIFICATION =
  "sellerAuthorizedNotification";
const MESSAGE_STATUS_PENDING = "pending";
const MESSAGE_STATUS_CREATED = "created";

class Message extends FirestoreBase {
  constructor(createdBy, tenant, brand, docType) {
    super(createdBy, tenant, brand, docType);
    this.props = {
      json_payload: "",
    };
    this.status = MESSAGE_STATUS_CREATED;
    this.name = "New Message";
    this.parentRef = null;
  }
}

/** Create a new message. This will kick off the message create trigger which will call the send message function.
 * The message will be sent via mailgun.When running in the emulator, the message will not be sent to a pubsub that can handle it.
 * To test end-to-end message functionality, run without the emulator using dev.
 * @param {object} auth Authentication provider to extract current user and tenant.
 * @param {string} customerName Name of customer config from the brand config. Caller can extract this from the global config provider.
 * @param {string} seller The seller document.
 * @param {array[string]} to Array of strings containing email addresses to send the message to.
 * @param {string} subject The subject of the email.
 * @param {string} htmlBody The body of the email. Must be surrounded by html tags.
 * @optionalParam {array[string]} cc Email addresses for cc.
 * @optionalParam {array[string]} bcc Email address for bcc.
 * @optionalParam {string} fromEmail Email address to send from.
 * @returns {Message} Returns the newly created message.
 */
const createAuthorizedSellerMessage = async (
  auth,
  customerName,
  seller,
  to,
  subject,
  htmlBody,
  cc = [],
  bcc = [],
  fromEmail = DEFAULT_SIGIL_LINKS.supportEmail,
) => {
  const payload = {
    messageTo: to,
    cc: cc,
    bcc: bcc,
    subject: subject,
    htmlBody: htmlBody,
    messageFrom: `${customerName} <${fromEmail}>`,
    user: auth.email,
    sellerName: seller.name,
    sellerIdentifiers: seller.identifiers,
    channel: CHANNEL_EMAIL,
    commClass: COMM_CLASS_CUSTOMER_DIRECT,
    commSubClass: COMM_SUBCLASS_AUTH_PRICE_WARNING,
  };

  return await createMessage(
    auth,
    payload,
    seller.sigilId,
    MESSAGE_EVENT_SELLER_COMMUNICATION,
  );
};

const createExportMessage = async (auth, options) => {
  const requestDate = moment().format("YYYY_MM_DD_HH_mm_ss");
  const { tenant } = auth;
  const { name, event } = options;
  const payload = {
    requestFileName: `${tenant}_${
      name?.toLowerCase() || "export"
    }_${requestDate}.csv`,
  };

  return await createMessage(auth, payload, null, event);
};

const createLaunchCasesMessage = async (auth, payload) => {
  return await createMessage(
    auth,
    payload,
    null,
    MESSAGE_TYPE_LAUNCH_CASES,
    MESSAGE_STATUS_PENDING,
  );
};

const createForeverLaunchEnabledMessage = async (auth) => {
  return await createMessage(
    auth,
    {},
    null,
    MESSAGE_TYPE_FOREVER_LAUNCH_ENABLED,
    MESSAGE_STATUS_CREATED,
  );
};

/**
 * Notify internal OPS when a seller is moved from unauthorized to authorized.
 * Writes a `sellerAuthorizedNotification` message; the engine renders the Slack
 * Block Kit body from the `slackOpsNotification` config document.
 * @param {object} auth Auth provider ({ email, tenant }).
 * @param {object} seller The seller being authorized ({ name, identifiers }).
 * @returns {Promise<object>} The created message object.
 */
const createSellerAuthorizedNotification = async (auth, seller) => {
  const payload = {
    sellers: [{ name: seller.name, identifiers: seller.identifiers }],
  };

  return await createMessage(
    auth,
    payload,
    null,
    MESSAGE_EVENT_SELLER_AUTHORIZED_NOTIFICATION,
  );
};

const createMessage = async (
  auth,
  payload,
  parent,
  type,
  status = MESSAGE_STATUS_CREATED,
) => {
  const message = new Message(auth.email, auth.tenant, auth.tenant, type);

  message.status = status;
  message.parentRef = parent;
  message.props.json_payload = JSON.stringify(payload);

  // Hacky way to create a single document.
  // Custom objects must be converted to json objects for firebase to save them.
  const messageObj = message.toObject();
  await bulkNew(COLLECTION_NAME, [messageObj]);

  return messageObj;
};

const selfServeUserAlert = async (
  auth,
  customerName,
  step,
  messageTo,
  survey,
  catalog,
) => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("No authenticated user");
  }

  const email = currentUser.email || "unknown@email.com";
  const stepInfo = stepDetails[step];
  const htmlBody = buildMainBody(
    step,
    stepInfo,
    customerName,
    email,
    survey,
    catalog,
  );

  const payload = {
    messageTo,
    cc: [],
    bcc: [],
    subject: `${stepInfo.emoji} Self-Serve Alert: ${customerName} - ${stepInfo.description}`,
    htmlBody: htmlBody,
    messageFrom: `Sigil Portal <no-reply@sigilpricing.com>`,
    user: email,
    channel: CHANNEL_EMAIL,
  };

  const idTokenResult = await currentUser.getIdTokenResult();
  const claimTenant = idTokenResult.claims.tenant;

  const tenant =
    typeof claimTenant === "string"
      ? claimTenant
      : Array.isArray(claimTenant)
        ? claimTenant[0]
        : SELF_SERVE_TENANT_ID;

  const authObject = {
    email,
    tenant,
  };

  return await createMessage(authObject, payload, null, MESSAGE_TYPE_GENERIC);
};

export {
  selfServeUserAlert,
  createAuthorizedSellerMessage,
  createExportMessage,
  createLaunchCasesMessage,
  createForeverLaunchEnabledMessage,
  createSellerAuthorizedNotification,
  createMessage,
  COLLECTION_NAME,
  MESSAGE_TYPE_FOREVER_LAUNCH_ENABLED,
  MESSAGE_STATUS_SUCCESS,
  MESSAGE_STATUS_FAILED,
  MESSAGE_STATUS_PENDING,
  MESSAGE_STATUS_CREATED,
  MESSAGE_EVENT_PRODUCT_IMPORT,
  MESSAGE_EVENT_EXPORT,
  MESSAGE_EVENT_EXPORT_AUTHORIZED_CASES,
  MESSAGE_EVENT_EXPORT_PRODUCTS,
  MESSAGE_EVENT_EXPORT_SELLERS,
  MESSAGE_EVENT_PRODUCT_UPDATE,
  MESSAGE_EVENT_PRODUCT_DELETE,
  MESSAGE_EVENT_OPS_NOTIFICATION,
  MESSAGE_EVENT_SELLER_AUTHORIZED_NOTIFICATION,
  Message,
};
